import { type FormEvent, useReducer, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import {
  AdminApiError,
  correctStockCapacity,
  recordPhysicalShortage,
  setStockAccepting,
} from "~/lib/admin-api";
import {
  buildPhysicalShortageRequest,
  buildStockAcceptingRequest,
  buildStockCapacityRequest,
  canExecutePhysicalShortage,
  canExecuteStockAccepting,
  canExecuteStockCapacity,
  createStockExecuteRequest,
  formatInventoryGrams,
  getReasonLengthStatus,
  initialPhysicalShortageState,
  initialStockAcceptingState,
  initialStockCapacityState,
  physicalShortageReducer,
  physicalShortageRecoveryForErrorCode,
  physicalShortageSelection,
  resolveStockPoolRow,
  stockAcceptingReducer,
  stockCapacityReducer,
} from "~/lib/admin-inventory";
import {
  dateDisplayProps,
  formatDate,
  formatDateTime,
  formatMillingType,
  formatVariety,
} from "~/lib/format";
import type {
  AdminStockPoolRow,
  RecordPhysicalShortageRequest,
} from "~/types/admin-inventory";

export type StockPoolDialogKind = "capacity" | "accepting" | "shortage";

interface StockPoolDialogProps {
  accessToken: string;
  kind: StockPoolDialogKind;
  target: AdminStockPoolRow;
  rows: readonly AdminStockPoolRow[];
  onClose: () => void;
  onCompleted: () => Promise<void>;
  onSwitchToShortage: () => void;
}

const controlClass =
  "rounded-none border-zinc-300 bg-white text-zinc-900 shadow-none focus-visible:border-amber-500 focus-visible:ring-amber-500/40";
const primaryButtonClass =
  "rounded-none border border-amber-500 bg-amber-500 text-stone-950 shadow-none hover:bg-amber-400 focus-visible:border-amber-500 focus-visible:ring-amber-500/40";
const secondaryButtonClass =
  "rounded-none border-zinc-300 bg-white text-zinc-900 shadow-none hover:bg-zinc-100 focus-visible:border-amber-500 focus-visible:ring-amber-500/40";
const panelClass = "grid gap-3 border border-zinc-300 bg-white p-4 text-sm";
const alertClass =
  "rounded-none border-amber-300 bg-white text-zinc-900 shadow-none";
const dialogClass =
  "max-h-[calc(100dvh-2rem)] max-w-3xl overflow-y-auto rounded-none border-zinc-300 bg-white text-zinc-900 shadow-none";

function errorMessage(error: Error): string {
  if (
    error instanceof AdminApiError &&
    (error.status === 401 || error.status === 403)
  ) {
    return "管理者権限がありません。再ログインしてください。";
  }
  return error.message;
}

function PoolSummary({ pool }: { pool: AdminStockPoolRow }) {
  return (
    <div className="grid grid-cols-2 gap-3 border border-zinc-300 p-4 text-sm sm:grid-cols-4">
      <p>
        品種
        <br />
        <strong>{formatVariety(pool.variety)}</strong>
      </p>
      <p>
        年産
        <br />
        <strong>{pool.crop_year}</strong>
      </p>
      <p>
        容量
        <br />
        <strong>{formatInventoryGrams(pool.capacity_brown_rice_g)}</strong>
      </p>
      <p>
        引当済み
        <br />
        <strong>{formatInventoryGrams(pool.reserved_brown_rice_g)}</strong>
      </p>
    </div>
  );
}

function ReasonLengthStatus({ id, value }: { id: string; value: string }) {
  const status = getReasonLengthStatus(value);
  return (
    <p
      id={id}
      className={
        status.exceeded > 0 ? "text-sm text-red-700" : "text-sm text-zinc-500"
      }
      aria-live="polite"
    >
      残り {status.remaining} 文字
      {status.exceeded > 0 ? `（${status.exceeded}文字超過）` : null}
    </p>
  );
}

function CapacityDialog(props: StockPoolDialogProps) {
  const pool = resolveStockPoolRow(props.target, props.rows);
  const [newCapacity, setNewCapacity] = useState(
    String(pool.capacity_brown_rice_g),
  );
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [state, dispatch] = useReducer(
    stockCapacityReducer,
    initialStockCapacityState,
  );
  const preview = useMutation({
    mutationFn: (request: Parameters<typeof correctStockCapacity>[1]) =>
      correctStockCapacity(props.accessToken, request),
    onMutate: () => dispatch({ type: "preview_started" }),
    onSuccess: (result, request) =>
      dispatch({ type: "preview_received", request, preview: result }),
    onError: async (error) => {
      if (
        error instanceof AdminApiError &&
        error.code === "stock_capacity_conflict"
      ) {
        dispatch({ type: "conflict_received" });
        await props.onCompleted();
      } else dispatch({ type: "preview_failed" });
    },
  });
  const execute = useMutation({
    mutationFn: (request: Parameters<typeof correctStockCapacity>[1]) =>
      correctStockCapacity(props.accessToken, request),
    onMutate: () => dispatch({ type: "execute_started" }),
    onSuccess: async (result) => {
      dispatch({ type: "execute_received", result });
      await props.onCompleted();
      props.onClose();
      toast.success(
        result.result === "noop"
          ? "容量はすでに同じ値です（変更なし）"
          : "年産在庫の容量を補正しました。",
      );
    },
    onError: async (error) => {
      if (
        error instanceof AdminApiError &&
        error.code === "stock_capacity_conflict"
      ) {
        dispatch({ type: "conflict_received" });
        await props.onCompleted();
      } else dispatch({ type: "execute_failed" });
    },
  });
  const busy = preview.isPending || execute.isPending;
  const belowReserved =
    /^\d+$/.test(newCapacity.trim()) &&
    Number(newCapacity) < pool.reserved_brown_rice_g;
  function changed() {
    setFormError(null);
    preview.reset();
    execute.reset();
    dispatch({ type: "input_changed" });
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = buildStockCapacityRequest(pool, newCapacity, reason);
    if (!validation.ok) {
      setFormError(
        validation.reason === "reason_too_long"
          ? "理由は500文字以内で入力してください。"
          : "新容量と理由を確認してください。",
      );
      return;
    }
    if (belowReserved) {
      setFormError("引当済みを下回る容量は、物理不足として記録してください。");
      return;
    }
    setFormError(null);
    preview.mutate(validation.request);
  }
  function confirm() {
    if (!canExecuteStockCapacity(state)) return;
    const request = createStockExecuteRequest(state);
    if (request) execute.mutate(request);
  }
  const mutationError = execute.error ?? preview.error;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) props.onClose();
      }}
    >
      <DialogContent
        className={dialogClass}
        aria-labelledby="stock-capacity-title"
        aria-describedby="stock-capacity-description"
        showCloseButton={!busy}
      >
        <DialogHeader>
          <DialogTitle id="stock-capacity-title">
            年産在庫の容量を補正
          </DialogTitle>
          <DialogDescription id="stock-capacity-description">
            棚差訂正や追加調達を、プレビューしてから確定します。
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 px-6">
          <PoolSummary pool={pool} />
          <p className="text-sm text-zinc-600">
            現在の残量:{" "}
            <strong>{formatInventoryGrams(pool.available_brown_rice_g)}</strong>
          </p>
        </div>
        <form className="grid gap-4 px-6" onSubmit={submit}>
          <div className="grid gap-2">
            <Label htmlFor="stock-capacity-new">新しい容量（玄米換算 g）</Label>
            <Input
              className={controlClass}
              id="stock-capacity-new"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={newCapacity}
              disabled={busy}
              onChange={(event) => {
                setNewCapacity(event.target.value);
                changed();
              }}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="stock-capacity-reason">理由</Label>
            <Textarea
              className={controlClass}
              id="stock-capacity-reason"
              value={reason}
              aria-describedby="stock-capacity-reason-length"
              disabled={busy}
              onChange={(event) => {
                setReason(event.target.value);
                changed();
              }}
              required
            />
            <ReasonLengthStatus
              id="stock-capacity-reason-length"
              value={reason}
            />
          </div>
          <Button
            className={primaryButtonClass}
            type="submit"
            disabled={busy || belowReserved}
          >
            {preview.isPending ? "確認中…" : "補正内容をプレビュー"}
          </Button>
        </form>
        <div className="grid gap-4 px-6">
          {belowReserved ? (
            <Alert className={alertClass}>
              <AlertDescription>
                引当済みを下回っています。
                <button
                  type="button"
                  className="underline underline-offset-4"
                  onClick={props.onSwitchToShortage}
                >
                  「物理不足を記録」へ切り替える
                </button>
              </AlertDescription>
            </Alert>
          ) : null}
          {formError ? (
            <Alert className={alertClass}>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          ) : null}
          {state.phase === "idle" && state.recovery === "conflict" ? (
            <Alert className={alertClass}>
              <AlertDescription>
                容量が変更されました。最新値でもう一度プレビューしてください。
              </AlertDescription>
            </Alert>
          ) : null}
          {mutationError &&
          !(
            mutationError instanceof AdminApiError &&
            mutationError.code === "stock_capacity_conflict"
          ) ? (
            <Alert className={alertClass}>
              <AlertDescription>{errorMessage(mutationError)}</AlertDescription>
            </Alert>
          ) : null}
          {state.phase === "ready" || state.phase === "executing" ? (
            <div className={panelClass}>
              <p>
                変更前:{" "}
                {formatInventoryGrams(
                  state.preview.previous_capacity_brown_rice_g,
                )}
              </p>
              <p>
                変更後:{" "}
                {formatInventoryGrams(state.preview.new_capacity_brown_rice_g)}
              </p>
              <p>
                変更後残量:{" "}
                {formatInventoryGrams(state.preview.available_after_g)}
              </p>
              {state.preview.result === "noop" ? (
                <p>変更はありません。</p>
              ) : (
                <label className="flex items-center gap-3 font-medium">
                  <input
                    type="checkbox"
                    className="size-4 accent-amber-500 outline-amber-500"
                    checked={state.phase === "ready" ? state.confirmed : true}
                    disabled={state.phase !== "ready"}
                    onChange={(event) =>
                      dispatch({
                        type: "confirm_toggled",
                        confirmed: event.target.checked,
                      })
                    }
                  />
                  表示した内容を確認しました
                </label>
              )}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            className={secondaryButtonClass}
            variant="outline"
            disabled={busy}
            onClick={props.onClose}
          >
            閉じる
          </Button>
          {(state.phase === "ready" || state.phase === "executing") &&
          state.preview.result !== "noop" ? (
            <Button
              className={primaryButtonClass}
              disabled={!canExecuteStockCapacity(state) || busy}
              onClick={confirm}
            >
              {execute.isPending ? "確定中…" : "容量補正を確定"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcceptingDialog(props: StockPoolDialogProps) {
  const pool = resolveStockPoolRow(props.target, props.rows);
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [state, dispatch] = useReducer(
    stockAcceptingReducer,
    initialStockAcceptingState,
  );
  const preview = useMutation({
    mutationFn: (request: Parameters<typeof setStockAccepting>[1]) =>
      setStockAccepting(props.accessToken, request),
    onMutate: () => dispatch({ type: "preview_started" }),
    onSuccess: (result, request) =>
      dispatch({ type: "preview_received", request, preview: result }),
    onError: () => dispatch({ type: "preview_failed" }),
  });
  const execute = useMutation({
    mutationFn: (request: Parameters<typeof setStockAccepting>[1]) =>
      setStockAccepting(props.accessToken, request),
    onMutate: () => dispatch({ type: "execute_started" }),
    onSuccess: async (result) => {
      dispatch({ type: "execute_received", result });
      await props.onCompleted();
      props.onClose();
      toast.success(
        result.result === "noop"
          ? "受付状態はすでに同じです（変更なし）"
          : result.new_accepting
            ? "新規受付を再開しました。"
            : "新規受付を停止しました。",
      );
    },
    onError: () => dispatch({ type: "execute_failed" }),
  });
  const busy = preview.isPending || execute.isPending;
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = buildStockAcceptingRequest(pool, reason);
    if (!validation.ok) {
      setFormError(
        validation.reason === "reason_too_long"
          ? "理由は500文字以内で入力してください。"
          : "理由を入力してください。",
      );
      return;
    }
    setFormError(null);
    preview.mutate(validation.request);
  }
  function confirm() {
    if (!canExecuteStockAccepting(state)) return;
    const request = createStockExecuteRequest(state);
    if (request) execute.mutate(request);
  }
  const mutationError = execute.error ?? preview.error;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) props.onClose();
      }}
    >
      <DialogContent
        className={dialogClass}
        aria-labelledby="stock-accepting-title"
        aria-describedby="stock-accepting-description"
        showCloseButton={!busy}
      >
        <DialogHeader>
          <DialogTitle id="stock-accepting-title">
            年産在庫の受付を変更
          </DialogTitle>
          <DialogDescription id="stock-accepting-description">
            新規予約の受付状態だけを変更します。既存の予約には影響しません。
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 px-6">
          <PoolSummary pool={pool} />
          <div className="grid grid-cols-2 gap-3 border border-zinc-300 p-4 text-sm">
            <p>
              現在
              <br />
              <strong>{pool.accepting_new ? "受付中" : "停止中"}</strong>
            </p>
            <p>
              変更後
              <br />
              <strong>{pool.accepting_new ? "停止中" : "受付中"}</strong>
            </p>
          </div>
        </div>
        <form className="grid gap-4 px-6" onSubmit={submit}>
          <div className="grid gap-2">
            <Label htmlFor="stock-accepting-reason">理由</Label>
            <Textarea
              className={controlClass}
              id="stock-accepting-reason"
              value={reason}
              aria-describedby="stock-accepting-reason-length"
              disabled={busy}
              onChange={(event) => {
                setReason(event.target.value);
                setFormError(null);
                preview.reset();
                execute.reset();
                dispatch({ type: "input_changed" });
              }}
              required
            />
            <ReasonLengthStatus
              id="stock-accepting-reason-length"
              value={reason}
            />
          </div>
          <Button className={primaryButtonClass} type="submit" disabled={busy}>
            {preview.isPending ? "確認中…" : "受付変更をプレビュー"}
          </Button>
        </form>
        <div className="grid gap-4 px-6">
          {formError ? (
            <Alert className={alertClass}>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          ) : null}
          {mutationError ? (
            <Alert className={alertClass}>
              <AlertDescription>{errorMessage(mutationError)}</AlertDescription>
            </Alert>
          ) : null}
          {state.phase === "ready" || state.phase === "executing" ? (
            <div className={panelClass}>
              <p>
                {state.preview.previous_accepting ? "受付中" : "停止中"} →{" "}
                <strong>
                  {state.preview.new_accepting ? "受付中" : "停止中"}
                </strong>
              </p>
              <p>理由: {state.request.reason}</p>
              {state.preview.result === "noop" ? (
                <p>変更はありません。</p>
              ) : null}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            className={secondaryButtonClass}
            variant="outline"
            disabled={busy}
            onClick={props.onClose}
          >
            閉じる
          </Button>
          {(state.phase === "ready" || state.phase === "executing") &&
          state.preview.result !== "noop" ? (
            <Button
              className={primaryButtonClass}
              disabled={!canExecuteStockAccepting(state) || busy}
              onClick={confirm}
            >
              {execute.isPending ? "確定中…" : "受付変更を確定"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShortageDialog(props: StockPoolDialogProps) {
  const pool = resolveStockPoolRow(props.target, props.rows);
  const [newCapacity, setNewCapacity] = useState("");
  const [reason, setReason] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectionDirty, setSelectionDirty] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [state, dispatch] = useReducer(
    physicalShortageReducer,
    initialPhysicalShortageState,
  );
  const candidates =
    state.phase === "ready" || state.phase === "executing"
      ? state.preview.candidates
      : [];
  const selection =
    state.phase === "ready" || state.phase === "executing"
      ? physicalShortageSelection(state.preview, selectedIds)
      : { selected_release_g: 0, covered: false };
  const preview = useMutation({
    mutationFn: (request: RecordPhysicalShortageRequest) =>
      recordPhysicalShortage(props.accessToken, request),
    onMutate: () => dispatch({ type: "preview_started" }),
    onSuccess: (result, request) => {
      setSelectionDirty(false);
      setSelectedIds(request.cycle_ids);
      dispatch({ type: "preview_received", request, preview: result });
    },
    onError: async (error) => {
      if (
        error instanceof AdminApiError &&
        error.code === "physical_shortage_conflict"
      ) {
        dispatch({ type: "conflict_received" });
        await props.onCompleted();
      } else dispatch({ type: "preview_failed" });
    },
  });
  const execute = useMutation({
    mutationFn: (request: RecordPhysicalShortageRequest) =>
      recordPhysicalShortage(props.accessToken, request),
    onMutate: () => dispatch({ type: "execute_started" }),
    onSuccess: async (result) => {
      dispatch({ type: "execute_received", result });
      await props.onCompleted();
      props.onClose();
      toast.success(
        result.skipped > 0
          ? `物理不足を記録し、${result.skipped}件をスキップしました。「定期便の回」タブで確認してください。`
          : "物理不足を記録しました。",
      );
    },
    onError: async (error) => {
      const recovery =
        error instanceof AdminApiError && error.status === 409
          ? physicalShortageRecoveryForErrorCode(error.code)
          : null;
      if (recovery === "conflict") {
        dispatch({ type: "conflict_received" });
        await props.onCompleted();
        return;
      }
      if (recovery === "not_covered") {
        dispatch({ type: "not_covered_received" });
        await props.onCompleted();
        return;
      }
      dispatch({ type: "execute_failed" });
    },
  });
  const busy = preview.isPending || execute.isPending;
  function firstPreview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = buildPhysicalShortageRequest(pool, newCapacity, reason);
    if (!validation.ok) {
      setFormError(
        validation.reason === "reason_too_long"
          ? "理由は500文字以内で入力してください。"
          : "現在容量より小さい新容量と理由を入力してください。",
      );
      return;
    }
    setFormError(null);
    setSelectedIds([]);
    preview.mutate(validation.request);
  }
  function verifySelection() {
    const validation = buildPhysicalShortageRequest(
      pool,
      newCapacity,
      reason,
      selectedIds,
      true,
    );
    if (validation.ok) preview.mutate(validation.request);
  }
  function toggleCycle(cycleId: string, checked: boolean) {
    setSelectedIds((current) =>
      checked
        ? [...new Set([...current, cycleId])].slice(0, 100)
        : current.filter((id) => id !== cycleId),
    );
    setSelectionDirty(true);
    dispatch({ type: "confirm_toggled", confirmed: false });
  }
  function confirm() {
    if (!canExecutePhysicalShortage(state) || selectionDirty) return;
    const request = createStockExecuteRequest(state);
    if (request) execute.mutate(request);
  }
  const mutationError = execute.error ?? preview.error;
  const recovery = state.phase === "idle" ? state.recovery : undefined;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) props.onClose();
      }}
    >
      <DialogContent
        className={dialogClass}
        aria-labelledby="physical-shortage-title"
        aria-describedby="physical-shortage-description"
        showCloseButton={!busy}
      >
        <DialogHeader>
          <DialogTitle id="physical-shortage-title">物理不足を記録</DialogTitle>
          <DialogDescription id="physical-shortage-description">
            新容量を確定し、必要な定期便の回を選び、受付停止を同じ処理で行います。
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 px-6">
          <PoolSummary pool={pool} />
        </div>
        <form className="grid gap-4 px-6" onSubmit={firstPreview}>
          <p className="font-semibold text-amber-700">1. 新しい実在庫容量</p>
          <div className="grid gap-2">
            <Label htmlFor="shortage-new-capacity">
              新しい容量（玄米換算 g）
            </Label>
            <Input
              className={controlClass}
              id="shortage-new-capacity"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={newCapacity}
              disabled={busy}
              onChange={(event) => {
                setNewCapacity(event.target.value);
                dispatch({ type: "input_changed" });
              }}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="shortage-reason">理由</Label>
            <Textarea
              className={controlClass}
              id="shortage-reason"
              value={reason}
              aria-describedby="shortage-reason-length"
              disabled={busy}
              onChange={(event) => {
                setReason(event.target.value);
                dispatch({ type: "input_changed" });
              }}
              required
            />
            <ReasonLengthStatus id="shortage-reason-length" value={reason} />
          </div>
          <Button className={primaryButtonClass} type="submit" disabled={busy}>
            {preview.isPending ? "確認中…" : "影響する回を取得"}
          </Button>
        </form>
        <div className="grid gap-4 px-6">
          {formError ? (
            <Alert className={alertClass}>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          ) : null}
          {recovery ? (
            <Alert className={alertClass}>
              <AlertDescription>
                {recovery === "conflict"
                  ? "在庫または候補の状態が変更されました。最新値でもう一度 dry-run してください。"
                  : "選択した回だけでは不足を解消できませんでした。最新の候補を取得し、選択し直してください。"}
              </AlertDescription>
            </Alert>
          ) : null}
          {mutationError &&
          !(
            mutationError instanceof AdminApiError &&
            mutationError.status === 409 &&
            physicalShortageRecoveryForErrorCode(mutationError.code) !== null
          ) ? (
            <Alert className={alertClass}>
              <AlertDescription>{errorMessage(mutationError)}</AlertDescription>
            </Alert>
          ) : null}
          {state.phase === "ready" || state.phase === "executing" ? (
            <>
              <section
                className="grid gap-3"
                aria-labelledby="shortage-candidates-heading"
              >
                <h3
                  id="shortage-candidates-heading"
                  className="font-semibold text-amber-700"
                >
                  2. スキップする回を選択
                </h3>
                <p className="text-sm text-zinc-600">
                  必要解放量{" "}
                  {formatInventoryGrams(state.preview.required_release_g)} /
                  選択解放量{" "}
                  {formatInventoryGrams(selection.selected_release_g)} / 選択{" "}
                  {selectedIds.length}件（上限100件）
                </p>
                <div
                  className="h-2 border border-zinc-300 bg-zinc-100"
                  role="progressbar"
                  aria-label="不足解消の進捗"
                  aria-valuemin={0}
                  aria-valuemax={Math.max(1, state.preview.required_release_g)}
                  aria-valuenow={Math.min(
                    selection.selected_release_g,
                    Math.max(1, state.preview.required_release_g),
                  )}
                >
                  <div
                    className="h-full bg-amber-500"
                    style={{
                      width: `${state.preview.required_release_g === 0 ? 100 : Math.min(100, (selection.selected_release_g / state.preview.required_release_g) * 100)}%`,
                    }}
                  />
                </div>
                <div className="max-h-72 overflow-y-auto border border-zinc-300">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th className="p-3">選択</th>
                        <th className="p-3">契約</th>
                        <th className="p-3">内容</th>
                        <th className="p-3">締切 / 配送開始</th>
                        <th className="p-3">解放量</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((candidate) => (
                        <tr
                          key={candidate.cycle_id}
                          className="border-t border-zinc-200"
                        >
                          <td className="p-3">
                            <input
                              aria-label={`${candidate.subscription_short_id} の回を選択`}
                              type="checkbox"
                              className="size-4 accent-amber-500 outline-amber-500"
                              checked={selectedIds.includes(candidate.cycle_id)}
                              disabled={
                                busy ||
                                (selectedIds.length >= 100 &&
                                  !selectedIds.includes(candidate.cycle_id))
                              }
                              onChange={(event) =>
                                toggleCycle(
                                  candidate.cycle_id,
                                  event.target.checked,
                                )
                              }
                            />
                          </td>
                          <td className="p-3">
                            {candidate.subscription_short_id}
                            {candidate.notice_sent_at ? (
                              <Badge className="rounded-none border-amber-300 bg-amber-50 text-amber-800 shadow-none">
                                通知済み
                              </Badge>
                            ) : null}
                          </td>
                          <td className="p-3">
                            {formatVariety(candidate.variety)} /{" "}
                            {formatMillingType(candidate.milling_type)}
                          </td>
                          <td className="p-3">
                            <span
                              {...dateDisplayProps(
                                candidate.cutoff_at,
                                formatDateTime,
                              )}
                            />{" "}
                            /{" "}
                            <span
                              {...dateDisplayProps(
                                candidate.delivery_window_start,
                                formatDate,
                              )}
                            />
                          </td>
                          <td className="p-3">
                            {formatInventoryGrams(candidate.brown_rice_eq_g)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {candidates.length === 0 &&
                state.preview.required_release_g > 0 ? (
                  <Alert className={alertClass}>
                    <AlertDescription>
                      対象にできる定期便の回がありません。単品予約などの処理を先に行ってください。
                    </AlertDescription>
                  </Alert>
                ) : null}
                <Button
                  className={secondaryButtonClass}
                  variant="outline"
                  disabled={busy || !selection.covered || !selectionDirty}
                  onClick={verifySelection}
                >
                  {selection.covered
                    ? selectionDirty
                      ? "選択内容を dry-run で確認"
                      : "選択内容を確認済み"
                    : "必要量を満たす回を選択してください"}
                </Button>
              </section>
              {!selectionDirty && state.preview.covered ? (
                <section
                  className={panelClass}
                  aria-labelledby="shortage-confirm-heading"
                >
                  <h3
                    id="shortage-confirm-heading"
                    className="font-semibold text-amber-700"
                  >
                    3. 受付停止と最終確認
                  </h3>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="size-4 accent-amber-500 outline-amber-500"
                      checked={state.request.stop_accepting}
                      disabled={state.phase !== "ready"}
                      onChange={(event) =>
                        dispatch({
                          type: "stop_accepting_toggled",
                          stopAccepting: event.target.checked,
                        })
                      }
                    />
                    新規受付を停止する
                  </label>
                  <label className="flex items-center gap-3 font-medium">
                    <input
                      type="checkbox"
                      className="size-4 accent-amber-500 outline-amber-500"
                      checked={state.phase === "ready" ? state.confirmed : true}
                      disabled={state.phase !== "ready"}
                      onChange={(event) =>
                        dispatch({
                          type: "confirm_toggled",
                          confirmed: event.target.checked,
                        })
                      }
                    />
                    容量補正・回のスキップ・受付状態を確認しました
                  </label>
                </section>
              ) : null}
            </>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            className={secondaryButtonClass}
            variant="outline"
            disabled={busy}
            onClick={props.onClose}
          >
            閉じる
          </Button>
          {(state.phase === "ready" || state.phase === "executing") &&
          !selectionDirty &&
          state.preview.covered ? (
            <Button
              className={primaryButtonClass}
              disabled={!canExecutePhysicalShortage(state) || busy}
              onClick={confirm}
            >
              {execute.isPending ? "確定中…" : "物理不足を記録"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StockPoolDialog(props: StockPoolDialogProps) {
  if (props.kind === "capacity") return <CapacityDialog {...props} />;
  if (props.kind === "accepting") return <AcceptingDialog {...props} />;
  return <ShortageDialog {...props} />;
}
